import React, { useState } from 'react';
import { buildAssetUrl } from '../lib/api';

/**
 * Marca de cada buzón de correo (2026-07-27).
 *
 * V2 (28/9/2026): ni los logos ni las iniciales viven ya en el código. El
 * logo de cada buzón se sube en Configuración («Logos de los buzones») y la
 * API de la bandeja lo manda en `logo`; las iniciales salen de la
 * declaración del buzón en el `.env` (`MAIL_<BUZÓN>_INICIALES`). Sin logo,
 * un monograma con las iniciales (o las del nombre del buzón): nunca una
 * imagen rota.
 *
 * El monograma no es un "por si acaso": es un cuadro con las iniciales bien
 * compuestas, para que dos buzones se lean como pareja y no como uno bueno y
 * otro a medias.
 */
const ESTILOS = {
  clinica: {
    // El azul del buzón principal, el mismo que ya usan las pastillas y la
    // barra lateral de cada correo — así el color identifica el buzón.
    fondo: 'bg-[#1D4ED8]',
    anillo: 'ring-[#1D4ED8]',
    tinte: 'bg-[#1D4ED8]/[0.06]',
  },
  instituto: {
    // Verde de fondo e iniciales doradas para el segundo buzón.
    fondo: 'bg-gradient-to-br from-[#0A2C1F] to-[#071F16]',
    texto: 'text-[#E4CB93]',
    anillo: 'ring-[#12724A]',
    tinte: 'bg-[#0A2C1F]/[0.05]',
  },
};

export const marcaBuzon = (clave) => ESTILOS[clave] || ESTILOS.clinica;

/** «Clínica Ejemplo» → «CE»: las iniciales cuando el buzón no declara las suyas. */
export function inicialesDe(texto) {
  const palabras = String(texto || '').trim().split(/\s+/).filter(Boolean);
  return palabras.slice(0, 3).map((p) => p[0].toUpperCase()).join('');
}

/**
 * Cuadro de marca. `size` en píxeles (36 en el selector, 28 en filas).
 * `cuenta` es el buzón tal como lo da GET /api/mail/cuentas ({key, label,
 * iniciales, logo}). Redondeado y recortado, para que el logo y el
 * monograma ocupen el mismo hueco exacto y las tarjetas queden alineadas.
 */
export default function MarcaBuzon({ clave, cuenta = null, size = 36, className = '' }) {
  const m = marcaBuzon(clave);
  const [fallo, setFallo] = useState(false);
  const estilo = { width: size, height: size };
  const logo = cuenta?.logo ? buildAssetUrl(cuenta.logo) : null;
  if (logo && !fallo) {
    // SOLO el logotipo, sin recuadro (2026-08-24, dirección): PNG transparente
    // flotando, con sombra premium que abraza la propia forma del logo.
    return (
      <img
        src={logo}
        alt=""                 /* decorativo: el nombre del buzón va al lado */
        aria-hidden="true"
        style={estilo}
        onError={() => setFallo(true)}
        className={`logo-flotante object-contain shrink-0 ${className}`}
      />
    );
  }
  const iniciales = (cuenta?.iniciales || inicialesDe(cuenta?.label) || '·').slice(0, 4);
  return (
    <span
      aria-hidden="true"
      style={estilo}
      className={`rounded-[10px] shrink-0 ${m.fondo} ${m.texto || 'text-white'} font-semibold
        inline-flex items-center justify-center tracking-tight ${className}`}
    >
      <span style={{ fontSize: Math.round(size * 0.30) }}>{iniciales}</span>
    </span>
  );
}
